import React, { useEffect, useMemo, useState } from 'react';
import JSZip from 'jszip';
import { ChevronLeft, ChevronRight, Download, Film, Image, PackageOpen } from 'lucide-react';
import { MEDIA_ASSET_SPECS, renderAsset, triggerDownload } from './MediaAssetStudio';
import { mediaAssetConfig } from './mediaAssetConfig';
import { MEDIA_CHANNELS, mediaPackageFilename } from './mediaPackageSchema';
import { triggerRemoteDownload } from './browserDownload';
import {
  allCopyFilePath,
  buildAllCopy,
  buildMediaPackageReadme,
  copyFilePath,
  imageFilePath,
  mainPreviewVideoFilePath,
  packageDataFilePath,
  storyCopyFilePath,
  storyVideoFilePath,
} from './mediaPackageZipLayout';

const ClientMediaPackageView = ({ mediaPackage, language = 'en' }) => {
  const [generatedAssets, setGeneratedAssets] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [isPreparing, setIsPreparing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedStoryVideoIndex, setSelectedStoryVideoIndex] = useState(0);
  const [error, setError] = useState('');
  const isPortuguese = String(language).toLowerCase().startsWith('pt');
  const packageData = mediaPackage?.packageData;
  const cloudAssets = mediaPackage?.assets || {};
  const campaignImage = cloudAssets.campaignImage?.url
    || packageData?.config?.campaign_image_url
    || packageData?.guide?.thumbnail_url
    || '';
  const partnerLogo = cloudAssets.partnerLogo?.url || packageData?.config?.partner_logo_url || '';
  const phoneFrame = cloudAssets.phoneFrame?.url || packageData?.config?.phone_frame_url || '';
  const phoneScreen = cloudAssets.phoneScreen?.url || packageData?.config?.phone_screen_url || '';

  useEffect(() => {
    if (!packageData) return undefined;
    let cancelled = false;
    const urls = [];

    const prepare = async () => {
      const storedPreviewUrls = Object.fromEntries(
        MEDIA_ASSET_SPECS
          .map((spec) => [spec.id, cloudAssets.generated?.[spec.id]?.url || ''])
          .filter(([, url]) => Boolean(url))
      );
      const missingSpecs = MEDIA_ASSET_SPECS.filter((spec) => !storedPreviewUrls[spec.id]);
      setPreviewUrls(storedPreviewUrls);
      setIsPreparing(missingSpecs.length > 0);
      setError('');
      if (missingSpecs.length === 0) return;
      try {
        const nextAssets = {};
        const nextUrls = { ...storedPreviewUrls };
        for (const spec of missingSpecs) {
          const canvas = document.createElement('canvas');
          const formatConfig = mediaAssetConfig(packageData.config, spec.id);
          const blob = await renderAsset(canvas, spec, formatConfig, campaignImage, partnerLogo, phoneFrame, phoneScreen);
          if (cancelled) return;
          nextAssets[spec.id] = blob;
          nextUrls[spec.id] = URL.createObjectURL(blob);
          urls.push(nextUrls[spec.id]);
        }
        setGeneratedAssets(nextAssets);
        setPreviewUrls(nextUrls);
      } catch (renderError) {
        if (!cancelled) setError(isPortuguese ? 'Não foi possível preparar todas as pré-visualizações.' : 'Not all previews could be prepared.');
      } finally {
        if (!cancelled) setIsPreparing(false);
      }
    };

    prepare();
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [campaignImage, isPortuguese, packageData, partnerLogo, phoneFrame, phoneScreen]);

  const visibleChannels = useMemo(() => (
    MEDIA_CHANNELS.filter((channel) => String(packageData?.drafts?.[channel.id] || '').trim())
  ), [packageData]);
  const storyVideos = useMemo(() => {
    const stories = Array.isArray(packageData?.config?.instagram_story_series)
      ? packageData.config.instagram_story_series
      : [];
    const videos = stories
      .filter((story) => story.rendered_video_url)
      .map((story, index) => ({
        id: story.id || `story-video-${index + 1}`,
        title: story.title || story.stop_name || `Story ${index + 1}`,
        url: story.rendered_video_url,
      }));
    const mainUrl = packageData?.config?.instagram_story_last_render_url || '';
    if (mainUrl && !videos.some((video) => video.url === mainUrl)) {
      videos.push({ id: 'main-story-video', title: 'Main Story video', url: mainUrl });
    }
    return videos;
  }, [packageData]);

  useEffect(() => {
    setSelectedStoryVideoIndex((current) => Math.max(0, Math.min(current, storyVideos.length - 1)));
  }, [storyVideos.length]);

  if (!packageData) {
    return (
      <section className="rounded-[24px] border border-white/60 bg-white/92 p-6 shadow-[0_18px_60px_rgba(1,43,57,0.14)]">
        <div className="flex items-center gap-3"><PackageOpen className="h-5 w-5 text-cyan-700" /><h2 className="text-lg font-semibold text-slate-900">Media Package</h2></div>
        <p className="mt-2 text-sm text-slate-600">{isPortuguese ? 'O Media Package deste guia ainda está a ser preparado.' : 'The Media Package for this guide is still being prepared.'}</p>
      </section>
    );
  }

  const addRemoteFile = async (zip, url, path, warnings) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      zip.file(path, await response.blob());
    } catch {
      warnings.push(path);
    }
  };

  const downloadPackage = async () => {
    setIsDownloading(true);
    setError('');
    try {
      if (mediaPackage?.zip?.url) {
        // Stream the published ZIP directly to disk. Buffering it and then joining
        // the chunks into a Blob can use more than twice the package size in RAM.
        triggerRemoteDownload(mediaPackage.zip.url, mediaPackageFilename(packageData, 'zip'));
        return;
      }
      const zip = new JSZip();
      const warnings = [];
      zip.file('README.txt', buildMediaPackageReadme(packageData));
      zip.file(packageDataFilePath(), `${JSON.stringify(packageData, null, 2)}\n`);
      zip.file(allCopyFilePath(), buildAllCopy(packageData, visibleChannels));

      visibleChannels.forEach((channel, index) => {
        zip.file(copyFilePath(channel, index), `${packageData.drafts[channel.id]}\n`);
      });
      MEDIA_ASSET_SPECS.forEach((spec, index) => {
        if (generatedAssets[spec.id]) {
          zip.file(imageFilePath(spec, index), generatedAssets[spec.id]);
        }
      });

      const stories = Array.isArray(packageData.config?.instagram_story_series)
        ? packageData.config.instagram_story_series
        : [];
      for (let index = 0; index < stories.length; index += 1) {
        const story = stories[index];
        zip.file(storyCopyFilePath(story, index), [
          story.title || '',
          '',
          story.narration || '',
          '',
          story.caption || '',
        ].join('\n'));
        await addRemoteFile(zip, story.rendered_video_url, storyVideoFilePath(story, index), warnings);
      }
      await addRemoteFile(zip, packageData.config?.instagram_story_last_render_url, mainPreviewVideoFilePath(), warnings);
      if (warnings.length) {
        zip.file('DOWNLOAD_WARNINGS.txt', `These optional files could not be downloaded:\n\n${warnings.join('\n')}\n`);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      triggerDownload(blob, mediaPackageFilename(packageData, 'zip'));
    } catch (downloadError) {
      console.error('Media Package download failed:', downloadError);
      setError(isPortuguese ? 'Não foi possível criar o ficheiro ZIP.' : 'The ZIP file could not be created.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-[24px] border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3"><PackageOpen className="h-5 w-5 text-cyan-700" /><h2 className="text-lg font-semibold text-slate-900">Media Package</h2></div>
            <p className="mt-2 text-sm text-slate-600">{isPortuguese ? 'Materiais e textos preparados para comunicar o lançamento do audioguia.' : 'Prepared visuals and copy for communicating the audio guide launch.'}</p>
            {mediaPackage.version && <p className="mt-1 text-xs font-semibold text-cyan-800">{isPortuguese ? 'Versão' : 'Version'} {mediaPackage.version}{mediaPackage.publishedAt ? ` · ${new Date(mediaPackage.publishedAt).toLocaleDateString(language)}` : ''}{mediaPackage.zip?.size ? ` · ${(mediaPackage.zip.size / (1024 * 1024)).toFixed(1)} MB` : ''}</p>}
          </div>
          <button type="button" onClick={downloadPackage} disabled={isDownloading || (isPreparing && !mediaPackage?.zip?.url)} className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white disabled:bg-slate-400">
            <Download className="h-4 w-4" />
            {isDownloading ? (isPortuguese ? 'A iniciar descarga...' : 'Starting download...') : (isPortuguese ? 'Descarregar Media Package' : 'Download Media Package')}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {MEDIA_ASSET_SPECS.map((spec) => (
          <React.Fragment key={spec.id}>
          <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex aspect-[4/3] items-center justify-center bg-slate-100 p-3">
              {previewUrls[spec.id]
                ? <img src={previewUrls[spec.id]} alt={spec.label} className="max-h-full max-w-full object-contain" />
                : <div className="flex items-center gap-2 text-sm text-slate-500"><Image className="h-4 w-4" />{isPreparing ? (isPortuguese ? 'A preparar...' : 'Preparing...') : (isPortuguese ? 'Pré-visualização indisponível' : 'Preview unavailable')}</div>}
            </div>
            <div className="border-t border-slate-200 p-4"><div className="font-semibold text-slate-900">{spec.label}</div><div className="mt-1 text-xs text-slate-500">{spec.width} × {spec.height}</div></div>
          </article>
          {spec.id === 'instagram_story' && (
            <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="relative flex aspect-[4/3] items-center justify-center bg-slate-950 p-3">
                {storyVideos.length > 0 ? (
                  <>
                    <video
                      key={storyVideos[selectedStoryVideoIndex]?.url}
                      src={storyVideos[selectedStoryVideoIndex]?.url}
                      controls
                      playsInline
                      preload="metadata"
                      className="h-full max-w-full rounded-lg object-contain"
                    />
                    {storyVideos.length > 1 && (
                      <>
                        <button type="button" aria-label="Previous Story video" onClick={() => setSelectedStoryVideoIndex((current) => (current - 1 + storyVideos.length) % storyVideos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/65 p-2 text-white hover:bg-black/85"><ChevronLeft className="h-4 w-4" /></button>
                        <button type="button" aria-label="Next Story video" onClick={() => setSelectedStoryVideoIndex((current) => (current + 1) % storyVideos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/65 p-2 text-white hover:bg-black/85"><ChevronRight className="h-4 w-4" /></button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-300"><Film className="h-4 w-4" />{isPortuguese ? 'Ainda não existem vídeos publicados.' : 'No published videos yet.'}</div>
                )}
              </div>
              <div className="border-t border-slate-200 p-4">
                <div className="font-semibold text-slate-900">Instagram / Facebook Story videos</div>
                {storyVideos.length > 0 && (
                  <div className="mt-1 flex items-center justify-between gap-3 text-xs text-slate-500">
                    <span className="truncate">{storyVideos[selectedStoryVideoIndex]?.title}</span>
                    <span className="shrink-0">{selectedStoryVideoIndex + 1} / {storyVideos.length}</span>
                  </div>
                )}
              </div>
            </article>
          )}
          </React.Fragment>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {visibleChannels.map((channel) => (
          <article key={channel.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">{channel.label}</div>
            <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{packageData.drafts[channel.id]}</div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default ClientMediaPackageView;
